import React, { FormEvent, useEffect, useState } from "react";
import api from "../../../api";
import '../../../styles/Login.scss'

interface User {
    id: number;
    name: string;
    email: string;
    role: string;
}

interface UserModalProps {
    user?: User | null;
    onClose: () => void;
    onSaved: () => void;
}

const UserModal = ({
                       user,
                       onClose,
                       onSaved,
                   }: UserModalProps) => {
    const isEditing = !!user;

    const [name, setName] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [role, setRole] = useState<string>("editor");
    const [password, setPassword] = useState<string>("");
    const [passwordConfirmation, setPasswordConfirmation] =
        useState<string>("");

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");

    useEffect(() => {
        if (user) {
            setName(user.name);
            setEmail(user.email);
            setRole(user.role);
        } else {
            setName("");
            setEmail("");
            setRole("editor");
            setPassword("");
            setPasswordConfirmation("");
        }

        setError("");
    }, [user]);

    const handleSubmit = async (
        event: FormEvent<HTMLFormElement>
    ) => {
        event.preventDefault();

        setLoading(true);
        setError("");

        try {
            if (isEditing) {
                await api.put(`/admin/users/${user.id}`, {
                    name,
                    email,
                    role,
                });

                if (password) {
                    await api.post(
                        `/admin/users/${user.id}/password`,
                        {
                            password,
                            password_confirmation: passwordConfirmation,
                        }
                    );
                }
            } else {
                await api.post("/admin/users", {
                    name,
                    email,
                    password,
                    role,
                });
            }

            onSaved();
            onClose();
        } catch (err: any) {
            console.error("Failed to save user:", err);

            const validationErrors = err.response?.data?.errors;

            if (validationErrors) {
                const firstError = Object.values(validationErrors)
                    .flat()
                    .find(
                        (error): error is string =>
                            typeof error === "string"
                    );

                setError(
                    firstError ||
                    "Unable to save user. Please try again."
                );
            } else {
                setError(
                    err.response?.data?.message ||
                    "Unable to save user. Please try again."
                );
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="admin-modal-overlay"
            onClick={onClose}
        >
            <div
                className="admin-modal"
                onClick={(event) => event.stopPropagation()}
            >

                <div className="admin-modal-header">

                    <h2>
                        {isEditing ? "Edit user" : "Add user"}
                    </h2>

                    <button
                        type="button"
                        className="admin-modal-close"
                        onClick={onClose}
                        disabled={loading}
                    >
                        &times;
                    </button>

                </div>

                <form onSubmit={handleSubmit}>

                    <div className="admin-modal-content">

                        <div className="form-group">
                            <label htmlFor="user-name">
                                Name
                            </label>

                            <input
                                id="user-name"
                                type="text"
                                value={name}
                                onChange={(event) =>
                                    setName(event.target.value)
                                }
                                required
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="user-email">
                                Email
                            </label>

                            <input
                                id="user-email"
                                type="email"
                                value={email}
                                onChange={(event) =>
                                    setEmail(event.target.value)
                                }
                                required
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="user-role">
                                Role
                            </label>

                            <select
                                id="user-role"
                                value={role}
                                onChange={(event) =>
                                    setRole(event.target.value)
                                }
                                disabled={loading}
                            >
                                <option value="editor">
                                    Editor
                                </option>

                                <option value="admin">
                                    Admin
                                </option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="user-password">
                                {isEditing
                                    ? "New password"
                                    : "Password"}
                            </label>

                            <input
                                id="user-password"
                                type="password"
                                value={password}
                                onChange={(event) =>
                                    setPassword(event.target.value)
                                }
                                required={!isEditing}
                                minLength={8}
                                autoComplete="new-password"
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="user-password-confirmation">
                                Confirm password
                            </label>

                            <input
                                id="user-password-confirmation"
                                type="password"
                                value={passwordConfirmation}
                                onChange={(event) =>
                                    setPasswordConfirmation(
                                        event.target.value
                                    )
                                }
                                required={!isEditing && !!password}
                                minLength={8}
                                autoComplete="new-password"
                                disabled={loading}
                            />
                        </div>

                        {error && (
                            <div className="form-error">
                                {error}
                            </div>
                        )}

                    </div>

                    <div className="admin-modal-actions">

                        <button
                            type="button"
                            className="admin-table-button"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            className="admin-primary-button"
                            disabled={loading}
                        >
                            {loading
                                ? "Saving..."
                                : isEditing
                                    ? "Save changes"
                                    : "Add user"}
                        </button>

                    </div>

                </form>

            </div>

        </div>
    );
};

export default UserModal;
